import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CalendarWidget from '../components/CalendarWidget';
import ServerOverview from '../components/ServerOverview';
import { Championship } from '../types';
import './HomePage.css';

const MIN_TOP = 30;

interface HomePageProps {
  broadcastPlatform?: 'youtube' | 'twitch' | '';
  broadcastChannel?: string;
  broadcastChannelId?: string;
  displayTimezone?: string;
  steamId?: string;
}

const HomePage: React.FC<HomePageProps> = ({ broadcastPlatform = '', broadcastChannel = '', broadcastChannelId = '', displayTimezone, steamId }) => {
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTop, setSidebarTop] = useState(MIN_TOP);
  const serverSectionRef = useRef<HTMLDivElement>(null);
  const initialTopRef = useRef(MIN_TOP);
  const navigate = useNavigate();

  const measureTop = useCallback(() => {
    if (serverSectionRef.current) {
      const section = serverSectionRef.current;
      const firstServerCardTop = section.querySelector('.server-card')?.getBoundingClientRect().top ?? section.getBoundingClientRect().top ?? 0;
      const top = firstServerCardTop + window.scrollY;
      initialTopRef.current = top;
      setSidebarTop(Math.max(MIN_TOP, top - window.scrollY));
    }
  }, []);

  // Measure once after mount and on window resize
  useEffect(() => {
    measureTop();
    window.addEventListener('resize', measureTop);
    return () => window.removeEventListener('resize', measureTop);
  }, [measureTop]);

  // Scroll listener always reads from the ref so it never needs re-registering
  useEffect(() => {
    const handleScroll = () => {
      setSidebarTop(Math.max(MIN_TOP, initialTopRef.current - window.scrollY));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="page-header">
      <h2>Server overview</h2>
      <div ref={serverSectionRef}>
        <ServerOverview
          onError={setError}
          onLoaded={measureTop}
          onSelectChampionship={(championship: Championship) => navigate(`/championships/${championship.id}`)}
          broadcastPlatform={broadcastPlatform}
          broadcastChannel={broadcastChannel}
          broadcastChannelId={broadcastChannelId}
          displayTimezone={displayTimezone}
        />
      </div>
      {error && <div className="error-message">{error}</div>}

      <div
        className={`homepage-sidebar${sidebarOpen ? ' sidebar-overlay-open' : ''}`}
        style={{ top: `${sidebarTop}px` }}
      >
        <CalendarWidget
          onSelectChampionship={(championshipId) => navigate(`/championships/${championshipId}`)}
          displayTimezone={displayTimezone}
          steamId={steamId}
        />
      </div>

      <button
        type="button"
        className="sidebar-toggle-btn"
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label={sidebarOpen ? 'Close calendar' : 'Open calendar'}
      >
        <Calendar size={18} />
      </button>

      {sidebarOpen && (
        <div className="sidebar-overlay-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default HomePage;
